// #region IMPORTS
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  detectPlatform,
  downloadFile,
  DOWNLOADS_URL,
  pickArtifact,
  pickDataUrl,
} from './offlineDownloadUtils';
// #endregion

// #region CONSTANTS
const MANIFEST_URL = `${DOWNLOADS_URL}manifest.json`;
// #endregion

// #region HELPERS
// Read the downloads manifest
const fetchManifest = () =>
  fetch(MANIFEST_URL, { cache: 'no-store' })
    .then(response => (response.ok ? response.json() : null))
    .then(manifest => (manifest && manifest.artifacts ? manifest : null))
    .catch(() => null);

// Detected OS when the manifest has its artifact, null otherwise
const defaultPlatform = manifest => {
  const platform = detectPlatform();

  return platform && manifest?.artifacts[platform] ? platform : null;
};
// #endregion

// #region HOOK
/**
 * State of the offline download panel: manifest, chosen OS, app download
 * trigger and the production data link.
 */
export const useOfflineDownload = () => {
  // #region INITIALIZATION
  const [manifest, setManifest] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [t] = useTranslation('Navbar');

  const canDownload = Boolean(manifest && platform);
  const dataUrl = pickDataUrl(manifest);
  // #endregion

  // #region EVENT
  // Download the app of the chosen OS through the browser
  const downloadApp = () => {
    const downloads = { manifest, downloadsUrl: DOWNLOADS_URL };
    const { url, file } = pickArtifact(downloads, platform);

    downloadFile(url, file);
  };

  // Start the app download, the panel stays open for the data section
  const handleDownload = () => {
    if (canDownload) {
      downloadApp();
    }
  };

  // Open the production data on Google Drive in a new tab
  const openData = () => window.open(dataUrl, '_blank', 'noopener,noreferrer');
  // #endregion

  // #region HOOK
  // Load the manifest and preselect the detected OS on mount
  useEffect(() => {
    fetchManifest().then(loaded => {
      setManifest(loaded);
      setPlatform(defaultPlatform(loaded));
    });
  }, []);
  // #endregion

  // #region INTERFACE
  return {
    t,
    manifest,
    platform,
    setPlatform,
    canDownload,
    dataUrl,
    handleDownload,
    openData,
  };
  // #endregion
};
// #endregion
